/**
 * The one ref every modal `<dialog>` in the player uses (D172).
 *
 * @remarks
 * Each dialog exists exactly while it is open, so the modal is opened as
 * the element attaches and closed as it detaches — the open flag and the
 * element cannot disagree, and `Escape` (which closes without a click)
 * still lands through `onClose`. A ref callback that RETURNS a cleanup is
 * never called with `null` in React 19, so there is no null branch to test.
 *
 * **Why a module constant and not an inline arrow.** React re-attaches a
 * callback ref whenever its IDENTITY changes: the old cleanup runs, the new
 * callback runs. Written inline, the callback was a new function on every
 * render — and the account dialog re-renders on every keystroke in its email
 * field and on every notice it is handed. Each render therefore CLOSED the
 * dialog (the cleanup), fired its `close` event, and the `onClose` handler
 * unmounted it: typing one letter dismissed the sign-in, and every door's
 * answer arrived to a dialog that had already gone (Mike, 2026-09-11). One
 * function, one identity, attached once.
 *
 * @packageDocumentation
 */

/**
 * Open the dialog as a modal while it is mounted, and close it as it leaves.
 *
 * @param dialog - The `<dialog>` element, as React hands it to a ref
 * @returns The cleanup React runs when the element detaches
 */
export const showModalWhileMounted = (dialog: HTMLDialogElement): (() => void) => {
	dialog.showModal();

	return () => {
		dialog.close();
	};
};
